import React, { useState, useEffect } from 'react';
import { Modal, Input, Alert } from 'antd';

/**
 * ConfirmDangerDialog — the single confirmation surface for any sensitive lifecycle
 * action (legal-hold release, erasure approval, compliance-service anonymization,
 * tenant/employee lifecycle transition). It can REQUIRE a typed reason (recorded as
 * audit evidence) and disables confirm until it's provided.
 *
 * It never implies more than it does — pass an honest `description` (e.g. "Only
 * compliance-service records are anonymized; other services are unaffected").
 */
export const ConfirmDangerDialog: React.FC<{
  open: boolean;
  title: string;
  description?: React.ReactNode;
  warning?: string;
  confirmLabel?: string;
  requireReason?: boolean;
  loading?: boolean;
  onConfirm: (reason: string) => void | Promise<void>;
  onCancel: () => void;
}> = ({
  open,
  title,
  description,
  warning,
  confirmLabel = 'Confirm',
  requireReason = true,
  loading = false,
  onConfirm,
  onCancel,
}) => {
  const [reason, setReason] = useState('');
  useEffect(() => {
    if (open) setReason('');
  }, [open]);

  const blocked = requireReason && reason.trim().length < 3;

  return (
    <Modal
      title={title}
      open={open}
      okText={confirmLabel}
      okType="danger"
      okButtonProps={{ disabled: blocked, loading }}
      onOk={() => onConfirm(reason.trim())}
      onCancel={onCancel}
      destroyOnClose
    >
      {description && <div style={{ marginBottom: 12 }}>{description}</div>}
      {warning && <Alert type="warning" showIcon message={warning} style={{ marginBottom: 12 }} />}
      {requireReason && (
        <>
          <div style={{ marginBottom: 6, fontWeight: 600 }}>Reason (recorded as audit evidence)</div>
          <Input.TextArea
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why is this action being taken?"
          />
        </>
      )}
    </Modal>
  );
};

export default ConfirmDangerDialog;
